import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { Table } from '../../../../components/Table'
import Badge from '../../../../components/Badge';
import Avatar from '../../../../components/Avatar/Avatar';
import { Column } from '../../../../types/table';
import AMSSelect from '../../../../components/AMSSelect';
import { hexToRgba } from '../../../../utils/helper';
import { CorporateTheme, font } from '../../../../utils/themes';
import StyledText from '../../../../components/StyledComponents/StyledText';
import Pagination from '../../../../components/listing/Pagination';
import { useAssignRolesToContactMutation } from '../../../../api/searchQueryApiSlice';
import { useSelector } from 'react-redux';
import { selectAuth } from '../../../auth/authSlice';
import { useGetSummaryDetailsQuery } from '../../../../api/portalApiSlice';
import TooltipIcon from '../../../../assets/icons/Tooltip';
import DropdownButton from '../../../../components/StyledComponents/StyledDropdownButton';
import OpitonsIcon from '../../../../assets/icons/OptionsIcon';
import AddIcon from '../../../../assets/icons/AddIcon';
import EditIcon from '../../../../assets/icons/EditIcon';
import RemoveIcon from '../../../../assets/icons/RemoveIcon';
import { on } from 'events';

type ApiRole = {
    id: number;
    name: string;
};

type ApiOrganizationContact = {
    id: number;
    name: string;
    email: string;
    phone: string | null;
    roles: ApiRole[];
    image: string | null;
    signedUrl: string;
    fields?: Record<string, any>;
};

type ListingContact = {
    id: number;
    name: string;
    email: string;
    roles: string[];
    phone: string;
    address: string;
    avatar?: string;
};

type OrganizationContactsResponse = {
    organizationId?: number;
    contacts?: ApiOrganizationContact[];
    totalCount?: number;
    page?: number;
    perPage?: number;
};

type ContactListingProps = {
    organizationContactsData?: OrganizationContactsResponse | ApiOrganizationContact[];
    contactsLoading?: boolean;
    contactsFetching?: boolean;
    currentPage: number;
    perPage: number;
    onPageChange: (page: number) => void;
    onLimitChange: (perPage: number) => void;
    refetchOrgContacts?: () => void;
    onAddContact?: () => void;
    onEditContact?: (contact: ApiOrganizationContact) => void;
    onRemoveContact?: (contact: ApiOrganizationContact) => void;
    roles?: {
        id: number; roleId: number;
        roleName: string;
    }[];
};

const ContactListing = ({
    organizationContactsData,
    contactsLoading = false,
    contactsFetching = false,
    currentPage,
    perPage,
    onPageChange,
    onLimitChange: handleLimitChange,
    refetchOrgContacts,
    onAddContact,
    onEditContact,
    onRemoveContact,
    roles
}: ContactListingProps) => {    
    const navigate = useNavigate();
    const hasRoles = Array.isArray(roles) && roles.length > 0;
    const hasAdminRole = hasRoles
        ? roles.some((role) => String(role?.roleName ?? '').toLowerCase() === 'admin')
        : false;

    const showManagedByAdminText = hasAdminRole;

    const [selectedRoles, setSelectedRoles] = useState<Record<number, string[]>>({});
    const [assignRoles] = useAssignRolesToContactMutation();
    const [loadingContactId, setLoadingContactId] = useState<number | null>(null);
    const [pendingRolesToSave, setPendingRolesToSave] = useState<Record<number, string[]>>({});

    const formatAddress = (fields?: Record<string, any>): string => {
        if (!fields || typeof fields !== 'object') {
            return '-';
        }

        const primaryRaw = (fields.contact_primary_address_type ?? '').toString().trim().toLowerCase();
        const addressType = primaryRaw === 'home' || primaryRaw === 'other' || primaryRaw === 'work'
            ? primaryRaw
            : 'work';

        const base = `contact_${addressType}_address_`;
        const line1 = (fields[`${base}line1`] ?? '').toString().trim();
        const line2 = (fields[`${base}line2`] ?? '').toString().trim();
        const city = (fields[`${base}city`] ?? '').toString().trim();
        const state = (fields[`${base}state`] ?? '').toString().trim().toUpperCase();
        const zip = (fields[`${base}zip`] ?? '').toString().trim();
        const country = (fields[`${base}country`] ?? '').toString().trim();

        const lines: string[] = [];

        if (line1) lines.push(line1);
        if (line2) lines.push(line2);

        if (city || state || zip) {
            let cityStateZip = '';
            if (city) cityStateZip += city;
            if (state || zip) {
                if (city) cityStateZip += ', ';
                if (state) cityStateZip += state;
                if (zip) {
                    if (state) cityStateZip += ' ';
                    cityStateZip += zip;
                }
            }
            if (cityStateZip) {
                lines.push(cityStateZip);
            }
        }

        if (country) lines.push(country);

        return lines.length > 0 ? lines.join('\t') : '-';
    };

    const apiContacts = useMemo<ApiOrganizationContact[]>(() => {
        if (Array.isArray(organizationContactsData)) {
            return organizationContactsData as ApiOrganizationContact[];
        }

        if (Array.isArray((organizationContactsData as OrganizationContactsResponse | undefined)?.contacts)) {
            return (organizationContactsData as OrganizationContactsResponse).contacts as ApiOrganizationContact[];
        }

        return [];
    }, [organizationContactsData]);

    const totalItems = useMemo(() => {
        if (Array.isArray(organizationContactsData)) {
            return organizationContactsData.length;
        }

        return (organizationContactsData as OrganizationContactsResponse | undefined)?.totalCount ?? apiContacts.length;
    }, [organizationContactsData, apiContacts.length]);

    const totalPages = useMemo(() => {
        if (perPage <= 0) {
            return 1;
        }
        return Math.max(1, Math.ceil(totalItems / perPage));
    }, [totalItems, perPage]);

    const contacts = useMemo<ListingContact[]>(() => {
        return apiContacts.map((contact) => ({
            id: contact.id,
            name: contact.name,
            email: contact.email,
            roles: Array.isArray(contact.roles) ? contact.roles.map((role) => role.name) : [],
            phone: contact?.fields?.contact_mobile_phone ?? '-',
            address: formatAddress(contact.fields),
            avatar: contact.signedUrl || undefined,
        }));
    }, [apiContacts]);

    const organizationId = useMemo(() => {
        if (Array.isArray(organizationContactsData)) {
            return undefined;
        }
        return (organizationContactsData as OrganizationContactsResponse)?.organizationId;
    }, [organizationContactsData]);

    const roleOptions = [
        { label: 'Contact', value: 'Contact' },
        { label: 'Billing Contact', value: 'Billing Contact' },
        { label: 'Primary Contact', value: 'Primary Contact' },
        { label: 'Technical Contact', value: 'Technical Contact' },
    ];

    const handleRoleChange = (contactId: number, selected: any) => {
        const values = Array.isArray(selected)
            ? selected.map((s: any) => s.value)
            : selected?.value ? [selected.value] : [];

        // Update local state for visual feedback (selected roles in dropdown)
        setSelectedRoles(prev => ({
            ...prev,
            [contactId]: values
        }));

        // Track pending roles to save when dropdown closes
        if (values.length > 0) {
            setPendingRolesToSave(prev => ({
                ...prev,
                [contactId]: values
            }));
        } else {
            setPendingRolesToSave(prev => {
                const updated = { ...prev };
                delete updated[contactId];
                return updated;
            });
        }
    };

    const handleRoleBlur = async (contactId: number) => {
        const pendingRoles = pendingRolesToSave[contactId];

        // Only call API if there are pending roles to save and organizationId exists
        if (pendingRoles && pendingRoles.length > 0 && organizationId) {
            setLoadingContactId(contactId);
            try {
                await assignRoles({
                    organizationId,
                    contactId,
                    roleIds: pendingRoles,
                }).unwrap();

                // Clear local state on success
                setSelectedRoles(prev => {
                    const updated = { ...prev };
                    delete updated[contactId];
                    return updated;
                });

                setPendingRolesToSave(prev => {
                    const updated = { ...prev };
                    delete updated[contactId];
                    return updated;
                });

                // Refetch the contact list
                onPageChange(currentPage);
                refetchOrgContacts?.();
            } catch (error) {
                console.error('Failed to assign roles:', error);
                // Keep selected roles in local state if API call fails
            } finally {
                setLoadingContactId(null);
            }
        }
    };

    const normalizeRolesValueForSelect = (rolesValue: any) => {
        if (Array.isArray(rolesValue)) {
            return rolesValue.map((item) => {
                if (typeof item === "object" && item !== null) {
                    const value = item.value ?? item.id ?? item.roleId ?? item;
                    const label = item.label ?? item.name ?? String(value ?? "");
                    return { value, label };
                } else {
                    // Assume it's an ID
                    return { value: item, label: String(item) };
                }
            });
        }
        if (!rolesValue) return [];
        if (rolesValue && typeof rolesValue === "object") {
            const value = (rolesValue as any).value ?? (rolesValue as any).id ?? (rolesValue as any).roleId ?? rolesValue;
            const label = (rolesValue as any).label ?? (rolesValue as any).name ?? String(value ?? "");
            return [{ value, label }];
        }
        return [{ value: rolesValue, label: String(rolesValue ?? "") }];
    };

    const columns: Array<Column<ListingContact>> = [
        {
            key: 'name',
            title: 'Name',
            width: '250px',
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Avatar
                        name={record.name}
                        imageUrl={record.avatar}
                        size={30}

                    />
                    <div>
                        <StyledText fontSize='13px' fontWeight={600} textColor={CorporateTheme.colors.headerColor}>{record.name}</StyledText>
                        <StyledText fontSize='12px' textColor={CorporateTheme.colors.textSecondary} fontWeight={400} fontFamily={font.lato}>Id: {record.id}</StyledText>
                    </div>
                </div>
            )
        },
        {
            key: 'email',
            title: 'Email Address',
            width: '250px',
            render: (email: string) => (
                <StyledText fontSize='13px' fontWeight={500} textColor={CorporateTheme.colors.textSecondary}>{email}</StyledText>
            )
        },
        {
            key: 'roles',
            title: 'Role',
            width: '200px',
            align: 'center',
            render: (roles: string[] | null | undefined, record: ListingContact) => {
                // Show dropdown if role is not assigned
                const assignedRoles = Array.isArray(roles) ? roles : [];
                if (assignedRoles.length === 0) {
                    if (!hasAdminRole) {
                        return (
                            <StyledText fontSize='13px' fontWeight={500} textColor={CorporateTheme.colors.textSecondary}>-</StyledText>
                        );
                    }

                    return (
                        <div
                            style={{ minWidth: '200px' }}
                            onClick={(e) => e.stopPropagation()}
                            onBlur={(e) => {
                                // Only handle blur if focus moved outside the container
                                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                                    handleRoleBlur(record.id);
                                }
                            }}
                            tabIndex={-1}
                        >
                            {/* <AMSSelect<typeof roleOptions[0], true>
                                staticOptions={roleOptions}
                                isMulti={true}
                                isSearchable
                                isClearable={true}
                                placeholder="Select roles..."
                                onChange={(selected) => handleRoleChange(record.id, selected)}
                                value={roleOptions.filter(opt => selectedRolesList.includes(opt.value))}
                                styles={{
                                    control: (base: any) => ({
                                        ...base,
                                        minHeight: '36px',
                                        height: '36px',
                                    })
                                }}
                            /> */
                            }
                            <AMSSelect
                                endpoint="/contact-roles"
                                placeholder="Select Roles"
                                isMulti
                                defaultOptions
                                isDisabled={loadingContactId === record.id}
                                value={normalizeRolesValueForSelect(selectedRoles[record.id] || [])}
                                onChange={(options) => handleRoleChange(record.id, options)}
                                mapResponse={(json: any, page: number) => {
                                    const roles = json?.roles || [];
                                    const totalCount = json?.totalCount ?? 0;
                                    const perPage = json?.perPage ?? json?.pagination?.perPage ?? 10;
                                    const hasMore = totalCount ? page * perPage < totalCount : roles.length === perPage;
                                    const options = roles.map((role: any) => ({
                                        label: role?.name ?? role?.label ?? String(role?.id ?? ""),
                                        value: role?.id ?? role?.value ?? role,
                                        ...role,
                                    }));
                                    return { options, hasMore, additional: { page: page + 1 } };
                                }}
                            />

                        </div>
                    );
                }

                // Show badge(s) if role is assigned
                const selectedRolesList = selectedRoles[record.id] || [];
                const allRoles = selectedRolesList.length > 0 ? selectedRolesList : assignedRoles;

                return (
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'center' }}>
                        {allRoles.map((r: string, index: number) => {
                            const color = index % 2 === 0 ? CorporateTheme.colors.primary : CorporateTheme.colors.count;
                            return (
                                <Badge
                                    key={`${r}-${index}`}
                                    text={r}
                                    bgColor={hexToRgba(color, 0.1)}
                                    color={color}
                                    padding="4px 12px"
                                    borderRadius="28px"
                                    fontSize="13px"
                                    fontWeight={500}
                                />
                            );
                        })}
                    </div>
                );
            }
        },
        {
            key: 'phone',
            title: 'Preferred Phone',
            width: '200px',
            align: 'center',
            render: (phone?: string) => (
                <StyledText fontSize='13px' fontWeight={500} textColor={CorporateTheme.colors.textSecondary}>{phone}</StyledText>
            )
        },
        {
            key: 'address',
            title: 'Preferred Address',
            align: 'center',
            render: (address: string) => (
                <StyledText fontSize='13px' fontWeight={500} textColor={CorporateTheme.colors.textSecondary}>{address}</StyledText>
            )
        },
        {
            key: 'actions',
            title: '',
            width: '80px',
            align: 'center',
            render: (_: any, record: ListingContact) => {
                if (showManagedByAdminText) return null;
                const apiContact = apiContacts.find((c) => c.id === record.id);
                if (!apiContact) return null;

                return (
                    <DropdownButton
                        icon={<OpitonsIcon color='var(--app-secondary-color)' size={18} rotation={90} />}
                        iconPosition="right"
                        buttonLabel=""
                        align="right"
                        removeborder
                        menuFontWeight={400}
                        fontSize='14px'
                        items={[
                            { label: "Edit Contact’s Details", onClick: () => onEditContact?.(apiContact), icon: <EditIcon color='var(--app-secondary-color)' size={19} /> },
                            { label: "Remove Contact", onClick: () => onRemoveContact?.(apiContact), icon: <RemoveIcon color='var(--app-secondary-color)' size={19} /> },
                        ]}
                    />
                );
            }
        },
    ];
    return (
        <div style={{ padding: 20, background: 'white' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #EBEAEB', paddingBottom: 15, marginBottom: 20 }}>
                <StyledText fontSize="15px" fontWeight={700} textColor={'var(--app-primary-color)'}>Contact Listing</StyledText>
                {!showManagedByAdminText ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <TooltipIcon color='var(--app-secondary-color)' />
                        <StyledText fontSize="13px" fontWeight={500} textColor={'var(--app-text-color)'} fontStyle='italic'>
                            Managed by your Organization Admin
                        </StyledText>
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => onAddContact?.()}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '8px 16px',
                            borderRadius: 8,
                            border: '1px solid var(--app-secondary-color)',
                            background: 'white',
                            cursor: 'pointer'
                        }}
                    >
                        <AddIcon color='var(--app-secondary-color)' size={20} />
                        <StyledText fontSize="13px" fontWeight={600} textColor={'var(--app-secondary-color)'}>
                            Add Contact
                        </StyledText>
                    </button>
                )}
            </div>

            <Table<ListingContact>
                columns={columns}
                data={contacts}
                loading={contactsLoading || contactsFetching}
                showActions={false}
                onRowClick={(record) => navigate(`?${new URLSearchParams({ page: 'contactDetails', id: String(record.id) }).toString()}`)}
                emptyMessage="No contacts found"
            />

            {(totalItems > 0 || currentPage > 1) && (
                <Pagination
                    className="mt-3 py-1"
                    currentPage={currentPage}
                    totalItems={totalItems}
                    perPage={perPage}
                    totalPages={totalPages}
                    onPageChange={(page: number) => {
                        if (page >= 1 && page <= totalPages) {
                            onPageChange(page);
                        }
                    }}
                    onLimitChange={(nextPerPage: number) => {
                        handleLimitChange(nextPerPage);
                    }}
                />
            )}
        </div>
    )
}

export default ContactListing
